import { compose, either, isEmpty, isNil, not, path } from 'ramda'
import getAttr from '../../utils/getAttr'
import machineDateFormatter from '../../utils/date/formatter/machineDateFormatter'

const isNotNilOrEmpty = compose(not, either(isEmpty, isNil))

/**
 * Actions factory.
 *
 * @param apiUrl
 * @param {FetchPath} fetchPath
 * @param {Function} requireAuthentication
 * @return {{xhrIndexOrders(): Promise<void>, xhrFetchOrder({commit: Function}, {id: (string|number)}): Promise}}
 */
export default ({
  apiUrl,
  fetchPath,
  requireAuthentication,
}) => ({
  /**
   * Fetches a single order from the server.
   *
   * @param {Function} commit
   * @param {Object} rootState
   * @param {string|number} id
   * @param {boolean} updateLoadingFlag
   * @return {Promise}
   */
  async xhrFetch({ commit, rootState }, { id, updateLoadingFlag = true }) {
    requireAuthentication({ rootState })

    if (updateLoadingFlag) {
      commit('SET_LOADING', true)
    }

    const entity = await fetchPath.run({
      path: `/yoda/api/orders/${id}`,
      jsonPath: ['content', 'data'],
    })

    await commit('UPDATE_ORDER', entity)
    commit('SET_LOADING', false)

    return entity
  },

  /**
   * Loads a page of orders.
   *
   * @param {Function} commit
   * @param {Object} rootState
   * @param {Object} state
   * @param {boolean} updateLoadingFlag
   * @param {Object} payload
   * @return {Promise}
   */
  async xhrIndex({ commit, state, rootState }, { updateLoadingFlag = true } = {}) {
    requireAuthentication({ rootState })

    if (updateLoadingFlag) {
      commit('SET_LOADING', true)
    }

    const parameters = { page: state.page + 1, itemsPerPage: state.pageSize }
    const createdFilter = path(['filters', 'created'], state)
    const invoiceFilter = path(['filters', 'invoice'], state)
    const statusFilter = path(['filters', 'statuses'], state)

    if (isNotNilOrEmpty(createdFilter)) {
      parameters['created[after]'] = compose(machineDateFormatter, path(['start']))(createdFilter)
      parameters['created[before]'] = compose(machineDateFormatter, path(['end']))(createdFilter)
    }
    if (isNotNilOrEmpty(invoiceFilter)) {
      parameters['invoiced[after]'] = compose(machineDateFormatter, path(['start']))(invoiceFilter)
      parameters['invoiced[before]'] = compose(machineDateFormatter, path(['end']))(invoiceFilter)
    }
    if (isNotNilOrEmpty(statusFilter)) {
      parameters.status = statusFilter
    }

    const result = await fetchPath.run({
      path: '/yoda/api/orders',
      jsonPath: ['content'],
      parameters,
    })

    await commit('SET_ORDERS', path(['data'], result))
    await commit('SET_ENTITY_COUNT', path(['meta', 'totalItems'], result))
    commit('SET_LOADING', false)

    return result
  },

  /**
   * Confirms an order.
   *
   * @param {Function} dispatch
   * @param {Object} rootState
   * @param {Object} order
   * @return {Promise}
   */
  async xhrConfirm({ dispatch, rootState }, { order }) {
    requireAuthentication({ rootState })

    const canConfirm = getAttr('type', order) === 'negotiation' && getAttr('status', order) === 'Approval Requested'
    const id = getAttr('id', order)

    if (canConfirm) {
      await fetchPath.run({
        path: `/yoda/api/sales-negotiations/${id}/approve`,
        method: 'PUT',
      })

      await dispatch('xhrFetch', { id, updateLoadingFlag: false })
      await dispatch('notifications/flash/create', { content: 'Order submitted', type: 'success' }, { root: true })
    } else {
      await dispatch('notifications/flash/create', { content: 'Unable to submit order', type: 'error' }, { root: true })
    }
  },

  /**
   * Sets the current page number.
   *
   * @param {Function} commit
   * @param {Number} page
   */
  setPage({ commit }, { page }) {
    commit('SET_PAGE', page)
  },

  /**
   * Sets the filters value.
   *
   * @param {Function} commit
   * @param {Object} filters
   */
  setFilters({ commit }, { filters }) {
    return commit('SET_FILTERS', filters)
  },

  async xhrPurchaseOrderFileUpload({ rootState }, file) {
    requireAuthentication({ rootState })
    const formData = new FormData()
    formData.append('file', file)

    const response = await fetch(
      `${apiUrl}/orders/upload-purchase-order-file`,
      {
        method: 'POST',
        body: formData,
      },
    )
    return response.json()
  },

  async xhrSubmitPurchaseOrderFile({ rootState }, data) {
    requireAuthentication({ rootState })

    await fetchPath.run({
      path: `/orders/submit-purchase-order-files`,
      jsonPath: ['content'],
      method: 'POST',
      parameters: {
        basketId: data?.basketId,
        orderId: data?.orderId,
        submissionId: data?.submissionId,
        files: JSON.stringify(data.files),
      },
    })
  },

  async xhrFetchPurchaseOrders({ rootState }, id) {
    requireAuthentication({ rootState })
    return fetchPath.run({
      path: `/yoda/api/orders/${id}/purchase-order-files`,
      jsonPath: ['content'],
    })
  },
})
